import React, { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import Layout from '../Layout'
import ResourceForm from '../../../Components/ResourceForm'
import FormFieldController from '../../../Components/FormFieldController'
import BackLink from '../../../Components/BackLink'
import CurrentImagePreview from '../../../Components/CurrentImagePreview'
import Button from '../../../Components/Button'
import { packageSchema } from '../../../schemas'
import { formatPrice } from '../../../utils/formatters'

const PRICE_TYPE_OPTIONS = [
  { value: 'per_person', label: 'Per person' },
  { value: 'flat', label: 'Flat cost' }
]

function AddOnRow({ addOn, packageId, onEdit, editing, onCancelEdit }) {
  const [name, setName] = useState(addOn.name)
  const [description, setDescription] = useState(addOn.description || '')
  const [pricePence, setPricePence] = useState(addOn.price_pence)
  const [priceType, setPriceType] = useState(addOn.price_type || 'per_person')
  const [active, setActive] = useState(addOn.active)

  const handleSave = () => {
    router.patch(`/manage/packages/${packageId}/package_add_ons/${addOn.id}`, {
      package_add_on: { name, description, price_pence: pricePence, price_type: priceType, active }
    })
    onCancelEdit()
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this add-on?')) {
      router.delete(`/manage/packages/${packageId}/package_add_ons/${addOn.id}`)
    }
  }

  if (editing) {
    return (
      <tr className="bg-sky-50">
        <td className="px-4 py-2">
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            className="w-full rounded border-gray-300 text-sm" />
        </td>
        <td className="px-4 py-2">
          <input type="text" value={description} onChange={e => setDescription(e.target.value)}
            className="w-full rounded border-gray-300 text-sm" />
        </td>
        <td className="px-4 py-2">
          <input type="number" value={pricePence} onChange={e => setPricePence(Number(e.target.value))}
            className="w-24 rounded border-gray-300 text-sm" />
        </td>
        <td className="px-4 py-2">
          <select value={priceType} onChange={e => setPriceType(e.target.value)}
            className="rounded border-gray-300 text-sm">
            {PRICE_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </td>
        <td className="px-4 py-2">
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
        </td>
        <td className="px-4 py-2 space-x-2">
          <Button link onClick={handleSave}>Save</Button>
          <Button link variant="muted" onClick={onCancelEdit}>Cancel</Button>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td className="px-4 py-3 font-medium text-gray-900">{addOn.name}</td>
      <td className="px-4 py-3 text-gray-500 truncate max-w-xs">{addOn.description}</td>
      <td className="px-4 py-3 text-gray-900">{formatPrice(addOn.price_pence)}</td>
      <td className="px-4 py-3 text-gray-500 capitalize">{addOn.price_type === 'flat' ? 'Flat' : 'Per person'}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
          addOn.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {addOn.active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-4 py-3 space-x-2">
        <Button link onClick={() => onEdit(addOn.id)}>Edit</Button>
        <Button link variant="danger" onClick={handleDelete}>Delete</Button>
      </td>
    </tr>
  )
}

function AddOnForm({ packageId }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [pricePence, setPricePence] = useState(0)
  const [priceType, setPriceType] = useState('per_person')
  const [showForm, setShowForm] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    router.post(`/manage/packages/${packageId}/package_add_ons`, {
      package_add_on: { name, description, price_pence: pricePence, price_type: priceType, active: true }
    })
    setName('')
    setDescription('')
    setPricePence(0)
    setPriceType('per_person')
    setShowForm(false)
  }

  if (!showForm) {
    return (
      <Button variant="secondary" onClick={() => setShowForm(true)}>
        + Add Add-on
      </Button>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} required
            className="w-full rounded border-gray-300 text-sm" placeholder="e.g., Flowers" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)}
            className="w-full rounded border-gray-300 text-sm" placeholder="Optional description" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Price (pence)</label>
          <input type="number" value={pricePence} onChange={e => setPricePence(Number(e.target.value))} min="0"
            className="w-full rounded border-gray-300 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Price type</label>
          <select value={priceType} onChange={e => setPriceType(e.target.value)}
            className="w-full rounded border-gray-300 text-sm">
            {PRICE_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit">Add</Button>
        <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
      </div>
    </form>
  )
}

export default function Edit({
  account,
  accounts,
  navigation,
  package: pkg,
  add_ons = [],
  event_types = []
}) {
  const [editingId, setEditingId] = useState(null)

  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title={`Edit: ${pkg.name}`}
    >
      <BackLink href="/manage/packages">Back to Packages</BackLink>

      <div className="bg-white shadow rounded-lg">
        <ResourceForm
          schema={packageSchema}
          resource="package"
          endpoint={`/manage/packages/${pkg.id}`}
          method="patch"
          backLink="/manage/packages"
          submitLabel="Save Changes"
          defaultValues={{
            name: pkg.name || '',
            description: pkg.description || '',
            base_price_pence: pkg.base_price_pence || '',
            min_guests: pkg.min_guests || 1,
            max_guests: pkg.max_guests || '',
            duration_minutes: pkg.duration_minutes || '',
            deposit_amount_pence: pkg.deposit_amount_pence || '',
            active: pkg.active ?? true,
            image: null,
            menu_document: null,
            event_type_ids: pkg.event_type_ids || []
          }}
        >
          {({ control, errors }) => (
            <>
              <FormFieldController
                control={control}
                name="name"
                label="Name"
                required
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="description"
                label="Description"
                type="textarea"
                rows={3}
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="base_price_pence"
                label="Base Price (per person)"
                type="currency"
                required
                errors={errors}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormFieldController
                  control={control}
                  name="min_guests"
                  label="Minimum Guests"
                  type="number"
                  errors={errors}
                />

                <FormFieldController
                  control={control}
                  name="max_guests"
                  label="Maximum Guests"
                  type="number"
                  errors={errors}
                />
              </div>

              <FormFieldController
                control={control}
                name="duration_minutes"
                label="Duration (minutes)"
                type="number"
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="deposit_amount_pence"
                label="Deposit Amount"
                type="currency"
                hint="Leave empty for no deposit. When set, guests pay this amount upfront when booking."
                errors={errors}
              />

              {event_types.length > 0 ? (
                <FormFieldController
                  control={control}
                  name="event_type_ids"
                  label="Event Types"
                  type="checkboxGroup"
                  options={event_types.map(et => ({ value: et.id, label: et.name }))}
                  hint="Occasions this package is suitable for"
                  errors={errors}
                />
              ) : (
                <div className="py-3">
                  <span className="block text-sm font-medium text-gray-700 mb-1">Event Types</span>
                  <p className="text-sm text-gray-500">
                    No event types yet.{' '}
                    <Link href="/manage/event_types/new" className="text-sky-600 hover:text-sky-800 underline">
                      Create one
                    </Link>{' '}
                    to tag this package for occasions like weddings or corporate days.
                  </p>
                </div>
              )}

              <CurrentImagePreview
                url={pkg.image_url}
                alt={pkg.name}
              />

              <FormFieldController
                control={control}
                name="image"
                label={pkg.image_url ? "Replace Image" : "Image"}
                type="file"
                accept="image/*"
                hint={pkg.image_url ? "Leave empty to keep current image" : undefined}
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="menu_document"
                label={pkg.menu_document_url ? "Replace Menu Document" : "Menu Document"}
                type="file"
                accept=".pdf,.doc,.docx"
                hint={pkg.menu_document_url ? "Leave empty to keep current document" : undefined}
                errors={errors}
              />

              <FormFieldController
                control={control}
                name="active"
                label="Active"
                type="checkbox"
                hint="Uncheck to hide this package from guests"
                errors={errors}
              />
            </>
          )}
        </ResourceForm>
      </div>

      {/* Add-ons Section */}
      <div className="bg-white shadow rounded-lg mt-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add-ons</h2>
        </div>

        {add_ons.length > 0 && (
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {add_ons.map(addOn => (
                  <AddOnRow
                    key={addOn.id}
                    addOn={addOn}
                    packageId={pkg.id}
                    editing={editingId === addOn.id}
                    onEdit={setEditingId}
                    onCancelEdit={() => setEditingId(null)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AddOnForm packageId={pkg.id} />
      </div>
    </Layout>
  )
}
